// src/features/changelog/components/ReleaseChangeList.jsx
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TYPE_COLORS = {
  feature: 'success',
  fix: 'error',
  improvement: 'primary',
  note: 'warning',
};

const DOT_SIZE = 8;
const BRANCH_WIDTH = 12;
const ITEM_PY = 4;
// Readable line length for the change text
const TEXT_MAX_WIDTH = '80ch';

// Git-log style tree: a trunk runs down from the group dot, each change
// curves off it (╰─), and the last branch ends the trunk.
const treeItemSx = {
  position: 'relative',
  py: `${ITEM_PY}px`,
  pl: `${BRANCH_WIDTH + 8}px`,
  maxWidth: TEXT_MAX_WIDTH,
  // Trunk segment alongside this item
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderLeft: 1,
    borderColor: 'divider',
  },
  // Curved branch to the middle of the item's first line
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    width: BRANCH_WIDTH,
    height: `calc(${ITEM_PY}px + 0.5lh)`,
    borderLeft: 1,
    borderBottom: 1,
    borderColor: 'divider',
    borderBottomLeftRadius: 8,
  },
  // The last branch ends the trunk
  '&:last-of-type::before': { display: 'none' },
};

export default function ReleaseChangeList({ groups }) {
  const { t } = useTranslation(['common']);

  return (
    <Stack spacing={2}>
      {groups.map(({ type, changes }) => {
        const color = `${TYPE_COLORS[type] ?? 'grey'}.main`;
        return (
          <Box key={type}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{ width: DOT_SIZE, height: DOT_SIZE, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
              />
              <Typography variant="subtitle2" sx={{ color, fontWeight: 700 }}>
                {t(`common:changelog.types.${type}`)}
              </Typography>
            </Box>
            <Box
              component="ul"
              // Trunk lines up with the centre of the dot
              sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0, ml: `${DOT_SIZE / 2 - 0.5}px` }}
            >
              {changes.map((change) => (
                <Typography
                  key={change.version}
                  component="li"
                  variant="body2"
                  title={`v${change.version}`}
                  sx={treeItemSx}
                >
                  {change.message}
                </Typography>
              ))}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
